import UiBtnGroup from "@/react/story/components/ui/UiBtnGroup";
import UiColorPicker from "@/react/story/components/ui/UiColorPicker";
import UiDropzone from "@/react/story/components/ui/UiDropzone";
import UiInput from "@/react/story/components/ui/UiInput";
import UiRange from "@/react/story/components/ui/UiRange";
import { ANIMATIONS, DEFAULTS, FONT_WEIGHTS, TEXT_ALIGNS } from "@/react/story/data";
import contentToInfos from "@/react/story/fns/content-to-infos";
import { useFonts } from "@/react/story/providers/FontsContext";
import { useLocales } from "@/react/story/providers/LocalesContext";
import { type Config, type Content, ContentTypeEnum, type Upload } from "@/react/story/types";
import TranslationUtils from "@/react/story/utils/TranslationUtils";
import { useState } from "react";

type Props = {
    content: Content;
    viewContent: (content: Content | null) => void;
    saveContent: (content: Content) => void;
    deleteContent: (content: Content) => void;
}

export default function ContentConfig(props: Props) {
    const Locales = useLocales();
    const Fonts = useFonts();

    const [ copied, setCopied ] = useState(false);

    const infos = contentToInfos(props.content, Locales.current);
    if (!infos) {
        return null;
    }

    const saveConfig = (config: Partial<Config>) => {
        props.saveContent({ ...props.content, config: { ...props.content.config, ...config } });
    };

    /** AUDIO stores a URL (or a legacy Upload) in `src`, ORIGIN a plain identifier */
    const getPlainValue = (): string | null => {
        if (ContentTypeEnum.ORIGIN === props.content.type) {
            return props.content.config.identifier ?? null;
        }

        const src = props.content.config.src;

        return "string" === typeof src ? src : src?.uri ?? null;
    };

    const onFileChange = (file: Upload | null) => {
        if (file) {
            saveConfig({ [infos.field]: file });
        } else {
            props.deleteContent(props.content);
        }
    };

    const getMain = () => {
        if (ContentTypeEnum.IMAGE === props.content.type) {
            return (
                <UiDropzone
                    defaultValue={props.content.config.image}
                    forceDelete={true}
                    onChange={onFileChange}
                />
            );
        }

        if (ContentTypeEnum.VIDEO === props.content.type) {
            return (
                <UiDropzone
                    defaultValue={props.content.config.video}
                    forceDelete={true}
                    onChange={onFileChange}
                    acceptedFiles="video/*"
                />
            );
        }

        return (
            <div className="input-group">
                <div className="input-group-text">
                    <span className={`bi-${infos.icon}`}></span>
                </div>
                {ContentTypeEnum.AUDIO === props.content.type || ContentTypeEnum.ORIGIN === props.content.type ? (
                    <UiInput
                        placeholder={infos.placeholder}
                        defaultValue={getPlainValue()}
                        autoFocus
                        onFocus={e => e.target.select()}
                        onChange={value => saveConfig({ [infos.field]: value })}
                    />
                ) : (
                    <UiInput
                        key={Locales.current}
                        type="textarea"
                        className="fs-3"
                        placeholder={infos.label}
                        defaultValue={props.content.config.text?.[Locales.current] ?? null}
                        autoFocus
                        onFocus={e => e.target.select()}
                        onChange={text => saveConfig({ text: { ...props.content.config.text, [Locales.current]: text } })}
                    />
                )}
                <button type="button" className="btn btn-danger" onClick={() => props.deleteContent(props.content)}>
                    <span className="bi-trash"></span>
                </button>
            </div>
        );
    };

    const canCopy = () => {
        return navigator
            && navigator.clipboard
            && "function" === typeof navigator.clipboard.writeText;
    };

    const handleCopy = (text: string | null) => {
        if (text) {
            void navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="d-flex flex-column gap-3">
            <div className="d-flex justify-content-start">
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => props.viewContent(null)}
                >
                    <span className="bi-arrow-left me-1"></span>
                    Retour
                </button>
            </div>
            {getMain()}
            {TranslationUtils.isTranslatable(props.content) && props.content.config.text ? (
                <>
                    {TranslationUtils.has(props.content.config.text, Locales.current) || !TranslationUtils.get(props.content.config.text, Locales.current) ? null : (
                        <div className="d-flex flex-column gap-1">
                            <p className="text-muted small mb-0">
                                <strong className="text-danger">Vous devez traduire</strong> : "{TranslationUtils.get(props.content.config.text, Locales.current)}"
                            </p>
                            {canCopy() ? (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm align-self-end d-inline-flex gap-1"
                                    onClick={() => handleCopy(TranslationUtils.get(props.content.config.text, Locales.current))}
                                >
                                    <span className="bi bi-copy" />
                                    {copied ? "Texte copié !" : "Copier le texte"}
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm align-self-end d-inline-flex gap-1"
                                onClick={() => saveConfig({
                                    text: {
                                        ...props.content.config.text,
                                        [Locales.current]: TranslationUtils.get(props.content.config.text, Locales.current),
                                    },
                                })}
                            >
                                <span className="bi bi-translate" />
                                Recopier dans le champ ci-dessus
                            </button>
                        </div>
                    )}
                    <div>
                        <strong className="d-block">Lien</strong>
                        <div className="input-group">
                            <div className="input-group-text">
                                <span className="bi-box-arrow-up-right"></span>
                            </div>
                            <UiInput
                                defaultValue={props.content.config.href ?? null}
                                onChange={href => saveConfig({ href })}
                            />
                        </div>
                    </div>
                </>
            ) : null}
            {ContentTypeEnum.AUDIO === props.content.type || ContentTypeEnum.ORIGIN === props.content.type ? null : (
                ContentTypeEnum.IMAGE === props.content.type || ContentTypeEnum.VIDEO === props.content.type ? (
                    <>
                        <div>
                            <strong className="d-block">Hauteur</strong>
                            <UiRange
                                min={20}
                                max={80}
                                defaultValue={props.content.config.height}
                                onChange={height => saveConfig({ height })}
                            />
                        </div>
                        <div>
                            <strong className="d-block">Largeur</strong>
                            <UiRange
                                defaultValue={props.content.config.maxWidth}
                                onChange={maxWidth => saveConfig({ maxWidth })}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <strong className="d-block">Couleur</strong>
                            <UiColorPicker
                                color={props.content.config.color}
                                onChange={color => saveConfig({ color })}
                            />
                        </div>
                        {ContentTypeEnum.LOCALE_PICKER === props.content.type ? null : (
                            <>
                                <div>
                                    <strong className="d-block">Couleur d'arrière plan</strong>
                                    <UiColorPicker
                                        color={props.content.config.backgroundColor}
                                        onChange={backgroundColor => saveConfig({ backgroundColor })}
                                    />
                                </div>
                                <div>
                                    <strong className="d-block">Marges horizontales (x{props.content.config.paddingX || DEFAULTS.paddingX})</strong>
                                    <UiRange
                                        max={5}
                                        defaultValue={props.content.config.paddingX || DEFAULTS.paddingX}
                                        onChange={paddingX => saveConfig({ paddingX })}
                                    />
                                </div>
                                <div>
                                    <strong className="d-block">Marges verticales (x{props.content.config.paddingY || DEFAULTS.paddingY})</strong>
                                    <UiRange
                                        max={5}
                                        defaultValue={props.content.config.paddingY || DEFAULTS.paddingY}
                                        onChange={paddingY => saveConfig({ paddingY })}
                                    />
                                </div>
                                <div>
                                    <strong className="d-block">Angle ({props.content.config.angle || DEFAULTS.angle}°)</strong>
                                    <UiRange
                                        min={-45}
                                        max={45}
                                        defaultValue={props.content.config.angle || DEFAULTS.angle}
                                        onChange={angle => saveConfig({ angle })}
                                    />
                                </div>
                                <div>
                                    <strong className="d-block">Bords arrondis</strong>
                                    <UiRange
                                        step={.2}
                                        min={0}
                                        max={3}
                                        defaultValue={props.content.config.borderRadius || DEFAULTS.borderRadius}
                                        onChange={borderRadius => saveConfig({ borderRadius })}
                                    />
                                </div>
                            </>
                        )}
                        <div>
                            <strong className="d-block">Alignement du texte</strong>
                            <UiBtnGroup
                                items={TEXT_ALIGNS}
                                isActive={v => v === props.content.config.textAlign}
                                set={textAlign => saveConfig({ textAlign: textAlign === props.content.config.textAlign ? null : textAlign })}
                            />
                            <p className="text-muted small mb-0 mt-1">
                                Sans choix, le texte suit l'alignement horizontal du bloc. Cliquer sur le choix actif l'annule.
                            </p>
                        </div>
                        {ContentTypeEnum.LOCALE_PICKER === props.content.type ? null : (
                            <>
                                {0 < Fonts.count ? (
                                    <div>
                                        <strong className="d-block">Police (famille)</strong>
                                        <div className="input-group">
                                            <div className="input-group-text"><span className="bi-alphabet-uppercase"></span></div>
                                            <select
                                                className="form-select"
                                                value={props.content.config.fontFamily}
                                                onChange={e => saveConfig({ fontFamily: e.target.value })}
                                            >
                                                <option value=""></option>
                                                {Fonts.all.map(f => (
                                                    <option key={f.family} value={f.family}>{f.family} ({f.weights.join(", ")})</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                ) : null}
                                <div>
                                    <strong className="d-block">Graisse de la police</strong>
                                    <UiBtnGroup
                                        items={FONT_WEIGHTS}
                                        isActive={v => v === props.content.config.fontWeight || (DEFAULTS.fontWeight === v && !props.content.config.fontWeight)}
                                        set={fontWeight => saveConfig({ fontWeight })}
                                    />
                                </div>
                                <div>
                                    <strong className="d-block">Taille de la police ({props.content.config.fontSize || DEFAULTS.fontSize}%)</strong>
                                    <UiRange
                                        step={5}
                                        min={50}
                                        max={200}
                                        defaultValue={props.content.config.fontSize || DEFAULTS.fontSize}
                                        onChange={fontSize => saveConfig({ fontSize })}
                                    />
                                </div>
                                <div>
                                    <strong className="d-block">Animation</strong>
                                    <select
                                        className="form-select"
                                        value={props.content.config.animation}
                                        onChange={e => saveConfig({ animation: e.target.value || null })}
                                    >
                                        {ANIMATIONS.map(a => (
                                            <option key={a.value} value={a.value}>
                                                {a.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {props.content.config.animation ? (
                                    <>
                                        <div>
                                            <strong className="d-block">Délai de l'animation ({props.content.config.animationDelay || DEFAULTS.animationDelay}ms)</strong>
                                            <UiRange
                                                step={100}
                                                min={0}
                                                max={5000}
                                                defaultValue={props.content.config.animationDelay || DEFAULTS.animationDelay}
                                                onChange={animationDelay => saveConfig({ animationDelay })}
                                            />
                                        </div>
                                        <div>
                                            <strong className="d-block">Durée de l'animation ({props.content.config.animationDuration || DEFAULTS.animationDuration}ms)</strong>
                                            <UiRange
                                                step={500}
                                                min={500}
                                                max={5000}
                                                defaultValue={props.content.config.animationDuration || DEFAULTS.animationDuration}
                                                onChange={animationDuration => saveConfig({ animationDuration })}
                                            />
                                        </div>
                                    </>
                                ) : null}
                            </>
                        )}
                    </>
                )
            )}
        </div>
    );
}
